import { useEffect, useState } from "react";
import axios from "axios";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

const inputCls =
  "rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white focus:border-pink-400 focus:outline-none";

export const BulkRequestsTab = ({ authH }) => {
  const [requests, setRequests] = useState([]);

  const load = () =>
    axios.get(`${API}/bulk-requests`, authH()).then((r) => setRequests(r.data)).catch(() => toast.error("Could not load bulk requests"));

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bulk-requests/${id}`, { status }, authH());
      toast.success(`Marked ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this bulk request?")) return;
    await axios.delete(`${API}/bulk-requests/${id}`, authH()).then(load).catch(() => toast.error("Delete failed"));
  };

  const pending = requests.filter((r) => r.status === "pending");

  return (
    <div data-testid="bulk-tab" className="space-y-5">
      <div className="rounded-2xl border border-pink-500/30 bg-pink-500/5 p-5">
        <h3 className="font-bold text-white">🍋 Bulk Order Requests</h3>
        <p className="text-sm text-zinc-400 mt-1">
          {pending.length} pending · {requests.length} total — submitted from the order form on the Sip page. Confirm, then mark completed when delivered.
        </p>
      </div>
      {requests.length === 0 && <p className="text-sm italic text-zinc-500">No bulk requests yet.</p>}
      <div className="space-y-4">
        {requests.map((r) => (
          <div key={r.id} className={`rounded-xl border p-4 space-y-3 ${r.status === "pending" ? "border-amber-400/40 bg-amber-400/5" : "border-white/10 bg-[#111]"}`} data-testid={`bulk-request-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-white">
                  {r.name} · <span className="text-zinc-400 font-normal">{r.phone}</span>
                </p>
                <p className="text-xs text-zinc-500">
                  {r.method}{r.need_date ? ` on ${r.need_date}` : ""} · ordered {r.order_date || new Date(r.created_at).toLocaleDateString()} · {new Date(r.created_at).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className={`${inputCls} !w-32`} data-testid={`bulk-status-${r.id}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => del(r.id)} className="rounded-lg bg-red-500/90 p-2 text-white hover:bg-red-500" data-testid={`bulk-delete-${r.id}`} aria-label="Delete request">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <ul className="text-sm text-zinc-300 space-y-1">
              {r.drinks.map((d, i) => (
                <li key={i} className="flex justify-between gap-4">
                  <span>{d.qty}× {d.name}</span><span className="text-zinc-400">${(d.qty * d.price).toFixed(2)}</span>
                </li>
              ))}
              {r.addons.map((a, i) => (
                <li key={`a-${i}`} className="flex justify-between gap-4 text-zinc-400">
                  <span>+ {a.qty}× {a.name}</span><span>${(a.qty * a.price).toFixed(2)}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap justify-between gap-2 text-sm pt-2 border-t border-white/10">
              <span className="text-zinc-500">
                Lemonades ${r.lemonades_total.toFixed(2)} · Add-ons ${r.addons_total.toFixed(2)} · Delivery {r.delivery_fee === 0 ? "Free" : `$${r.delivery_fee.toFixed(2)}`}
                {r.payment_methods.length ? ` · Pays: ${r.payment_methods.join(", ")}` : ""}
              </span>
              <span className="font-bold text-amber-300" data-testid={`bulk-total-${r.id}`}>${r.total_due.toFixed(2)}</span>
            </div>
            {r.notes && <p className="text-xs text-zinc-500 italic">"{r.notes}"</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BulkRequestsTab;
